<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { AlertTriangle, ArrowRight, CalendarClock, X } from "lucide-vue-next";
import { fullDate, shortDate } from "@/utils/dates";
import type { ScheduleImpact } from "@/types/models";

const props = defineProps<{
  impact: ScheduleImpact | null;
  canExtend?: boolean;
}>();
const emit = defineEmits<{
  close: [];
  apply: [options?: { extensionReason?: string }];
}>();
const dialog = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
const extensionReason = ref("");
const applyDisabled = computed(() => {
  if (!props.impact || props.impact.canApply === false) return true;
  if (!props.impact.crossesBoundary) return false;
  return !props.canExtend || extensionReason.value.trim().length < 4;
});
let returnFocus: HTMLElement | null = null;
let priorOverflow = "";
watch(
  () => props.impact,
  async (impact) => {
    if (impact) {
      extensionReason.value = "";
      returnFocus = document.activeElement as HTMLElement;
      priorOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      await nextTick();
      closeButton.value?.focus();
    } else {
      document.body.style.overflow = priorOverflow;
      returnFocus?.focus();
    }
  },
);
onBeforeUnmount(() => {
  document.body.style.overflow = priorOverflow;
});
function trapKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key !== "Tab" || !dialog.value) return;
  const items = [
    ...dialog.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ];
  if (!items.length) return;
  if (event.shiftKey && document.activeElement === items[0]) {
    event.preventDefault();
    items.at(-1)?.focus();
  } else if (!event.shiftKey && document.activeElement === items.at(-1)) {
    event.preventDefault();
    items[0]?.focus();
  }
}
function applyChange() {
  if (!props.impact || applyDisabled.value) return;
  emit(
    "apply",
    props.impact.crossesBoundary
      ? { extensionReason: extensionReason.value.trim() }
      : undefined,
  );
}
</script>

<template>
  <Teleport to="body">
    <div v-if="impact" class="impact-layer">
      <button
        class="impact-scrim"
        type="button"
        aria-label="Keep current dates"
        @click="emit('close')"
      ></button>
      <section
        ref="dialog"
        class="impact-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="impact-title"
        @keydown="trapKeys"
      >
        <header>
          <span class="impact-icon"><CalendarClock :size="21" /></span>
          <div>
            <h2 id="impact-title">Review date changes</h2>
            <p>
              {{
                impact.affected.length
                  ? `${impact.affected.length} dependent ${impact.affected.length === 1 ? "task moves" : "tasks move"} with this change.`
                  : "Only this task changes."
              }}
            </p>
          </div>
          <button
            ref="closeButton"
            class="icon-button"
            type="button"
            aria-label="Close date change preview"
            @click="emit('close')"
          >
            <X :size="20" />
          </button>
        </header>
        <div class="impact-body">
          <div class="primary-change">
            <strong>{{ impact.taskTitle }}</strong
            ><span
              ><time
                >{{ shortDate(impact.oldStart) }} –
                {{ shortDate(impact.oldDue) }}</time
              ><ArrowRight :size="16" /><time
                >{{ shortDate(impact.newStart) }} –
                {{ shortDate(impact.newDue) }}</time
              ></span
            >
          </div>
          <div v-if="impact.crossesBoundary" class="boundary-alert">
            <AlertTriangle :size="19" />
            <div>
              <strong>These dates cross the project target.</strong>
              <p>
                The current target is {{ fullDate(impact.targetEndDate) }}.
                {{
                  canExtend
                    ? `Extend it to ${fullDate(impact.requiredTargetEndDate || impact.newDue)} and record the reason before applying these changes.`
                    : "A project owner must extend it and record a reason before these changes can be applied."
                }}
              </p>
            </div>
          </div>
          <div
            v-if="impact.canApply === false"
            class="boundary-alert boundary-alert--conflict"
            role="alert"
          >
            <AlertTriangle :size="19" />
            <div>
              <strong>These date changes need attention.</strong>
              <p>
                {{
                  impact.violationMessages?.[0] ||
                  "Resolve the schedule conflict before applying these dates."
                }}
              </p>
            </div>
          </div>
          <div v-if="impact.affected.length" class="affected-list">
            <h3>Dependent work</h3>
            <div v-for="item in impact.affected" :key="item.taskId">
              <span
                ><strong>{{ item.title }}</strong
                ><small>{{ item.owner }}</small></span
              ><span class="tabular"
                >{{ shortDate(item.oldDue) }} <ArrowRight :size="14" />
                <strong>{{ shortDate(item.newDue) }}</strong></span
              >
            </div>
          </div>
          <div class="notification-note">
            Owners of moved work will receive one schedule-change notification
            after you apply the dates.
          </div>
          <div
            v-if="
              impact.crossesBoundary && canExtend && impact.canApply !== false
            "
            class="field"
          >
            <label for="extension-reason"
              >Reason for extending the project target</label
            ><textarea
              id="extension-reason"
              v-model="extensionReason"
              class="textarea"
              placeholder="Explain why the additional time is needed."
            ></textarea
            ><span class="helper"
              >Enter at least four characters. This reason is recorded with the
              schedule change.</span
            >
          </div>
        </div>
        <footer>
          <button
            class="button button--secondary"
            type="button"
            @click="emit('close')"
          >
            Keep current dates</button
          ><button
            class="button button--primary"
            type="button"
            :disabled="applyDisabled"
            @click="applyChange"
          >
            {{
              impact.canApply === false
                ? "Resolve schedule conflict"
                : impact.crossesBoundary
                  ? "Extend target and apply"
                  : "Apply date changes"
            }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.impact-layer {
  position: fixed;
  inset: 0;
  z-index: 130;
  display: grid;
  place-items: center;
  padding: 20px;
}
.impact-scrim {
  position: absolute;
  inset: 0;
  width: 100%;
  background: rgba(8, 27, 46, 0.52);
}
.impact-dialog {
  position: relative;
  width: min(650px, 100%);
  max-height: calc(100vh - 40px);
  display: grid;
  grid-template-rows: auto 1fr auto;
  border-radius: 10px;
  overflow: hidden;
  background: white;
  box-shadow: var(--shadow-panel);
}
.impact-dialog > header {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 12px;
  padding: 20px 22px;
  border-bottom: 1px solid var(--rule);
}
.impact-dialog header p {
  margin-top: 3px;
  color: var(--text-soft);
}
.impact-icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 7px;
  color: var(--shared);
  background: var(--shared-soft);
}
.impact-body {
  overflow: auto;
  display: grid;
  gap: 18px;
  padding: 22px;
}
.primary-change {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--rule);
}
.primary-change > span {
  display: flex;
  align-items: center;
  gap: 8px;
}
.primary-change time:last-child {
  color: var(--shared);
  font-weight: 700;
}
.boundary-alert {
  display: flex;
  gap: 11px;
  padding: 13px;
  border-radius: 6px;
  color: #70470c;
  background: var(--warning-soft);
}
.boundary-alert--conflict {
  color: #7d2828;
  background: #fff0f0;
}
.boundary-alert svg {
  flex: 0 0 auto;
}
.boundary-alert p {
  margin-top: 3px;
}
.affected-list {
  display: grid;
}
.affected-list h3 {
  margin-bottom: 8px;
}
.affected-list > div {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid var(--rule);
}
.affected-list > div > span:first-child {
  display: grid;
}
.affected-list small {
  color: var(--text-soft);
}
.affected-list > div > span:last-child {
  display: flex;
  align-items: center;
  gap: 6px;
}
.notification-note {
  padding: 11px 12px;
  border-radius: 6px;
  color: #174f7c;
  background: var(--shared-soft);
}
.impact-dialog > footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 13px 22px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
@media (max-width: 560px) {
  .impact-layer {
    padding: 0;
  }
  .impact-dialog {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }
  .primary-change,
  .affected-list > div {
    align-items: flex-start;
    flex-direction: column;
  }
  .impact-dialog > footer {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .impact-dialog .button {
    height: auto;
  }
}
</style>
